import { supabase } from '../lib/supabase';
import { DatabaseError } from '../lib/errors';

export interface UserNotification {
  id: string;
  user_id: string;
  organisation_id: string;
  type: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  reference_type: string;
  reference_id: string;
  link: string | null;
  due_date: string | null;
  is_read: boolean;
  is_archived: boolean;
  read_at: string | null;
  archived_at: string | null;
  created_at: string;
}

export async function getUserNotifications(userId: string) {
  const { data, error } = await supabase
    .from('user_notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw new DatabaseError('loading notifications', error);
  return data as UserNotification[];
}

export async function getUnreadNotifications(userId: string) {
  const { data, error } = await supabase
    .from('user_notifications')
    .select('*')
    .eq('user_id', userId)
    .eq('is_read', false)
    .eq('is_archived', false)
    .order('created_at', { ascending: false });

  if (error) throw new DatabaseError('loading notifications', error);
  return data as UserNotification[];
}

export async function markNotificationAsRead(notificationId: string) {
  const { data, error } = await supabase
    .from('user_notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString()
    })
    .eq('id', notificationId)
    .select()
    .single();

  if (error) throw new DatabaseError('loading notifications', error);
  return data;
}

export async function markNotificationAsArchived(notificationId: string) {
  const { data, error} = await supabase
    .from('user_notifications')
    .update({
      is_archived: true,
      archived_at: new Date().toISOString()
    })
    .eq('id', notificationId)
    .select()
    .single();

  if (error) throw new DatabaseError('loading notifications', error);
  return data;
}

export async function markAllAsRead(userId: string) {
  const { data, error } = await supabase
    .from('user_notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString()
    })
    .eq('user_id', userId)
    .eq('is_read', false)
    .select();

  if (error) throw new DatabaseError('loading notifications', error);
  return data;
}

export async function deleteNotification(notificationId: string) {
  const { error } = await supabase
    .from('user_notifications')
    .delete()
    .eq('id', notificationId);

  if (error) throw new DatabaseError('loading notifications', error);
}

export async function getNotificationCounts(userId: string) {
  const { data, error } = await supabase
    .from('user_notifications')
    .select('priority, is_read, is_archived, due_date')
    .eq('user_id', userId);

  if (error) throw new DatabaseError('loading notifications', error);

  const total = data.length;
  const unread = data.filter(n => !n.is_read && !n.is_archived).length;
  const critical = data.filter(n => n.priority === 'critical' && !n.is_archived).length;
  const overdue = data.filter(n => {
    if (!n.due_date || n.is_archived) return false;
    return new Date(n.due_date) < new Date();
  }).length;

  return { total, unread, critical, overdue };
}

export function subscribeToNotifications(userId: string, callback: (payload: Record<string, unknown>) => void) {
  const channel = supabase
    .channel(`notifications-${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'user_notifications',
        filter: `user_id=eq.${userId}`
      },
      callback
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
